import { useEffect, useState } from "react";

/**
 * Apple-style "Compact" size class detection.
 *
 *   Compact — iPhone-sized viewports (portrait AND landscape).
 *   Regular — iPad-sized viewports (portrait AND landscape) and desktop.
 *
 * The media query mirrors the `regular` Tailwind screen defined in
 * `tailwind.config.js`: the viewport must be at least 640px wide AND 500px
 * tall to count as Regular. iPhone portrait fails the width check; iPhone
 * landscape fails the height check (iPhone 16 Pro Max landscape is only
 * 430px tall). Both iPad orientations satisfy both checks.
 *
 * Pair with the Tailwind `regular:` prefix for CSS-only layout decisions;
 * reach for this hook when the decision needs to be made in JS (conditional
 * rendering, coerced state, etc.).
 */
export const COMPACT_MEDIA_QUERY =
  "not all and (min-width: 640px) and (min-height: 500px)";

export default function useIsCompact(): boolean {
  const [isCompact, setIsCompact] = useState<boolean>(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia(COMPACT_MEDIA_QUERY).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(COMPACT_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsCompact(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isCompact;
}

/**
 * Narrow-viewport detection for layouts that have a wide right pane after
 * subtracting the AppSidebar rail (52px) plus a secondary BrowserPanel
 * (~264px expanded). At <= 900px viewport width those side panels eat
 * enough horizontal space that wide grids (6+ columns) overflow. Triggers
 * on phones (any orientation) and iPad mini portrait (768px).
 *
 * Use this together with `useIsCompact` when a layout needs to switch
 * from a desktop grid to vertical cards earlier than the strict phone
 * threshold — i.e. when iPad portrait should also get the card layout.
 */
const NARROW_VIEWPORT_MEDIA_QUERY = "(max-width: 900px)";

export function useIsNarrowViewport(): boolean {
  const [isNarrow, setIsNarrow] = useState<boolean>(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia(NARROW_VIEWPORT_MEDIA_QUERY).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(NARROW_VIEWPORT_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isNarrow;
}

/**
 * Short-height detection for phone landscape (any phone, rotated). Useful
 * when a popover or sheet would otherwise overrun the bottom of the
 * viewport — on these screens we typically detach from the trigger and
 * pin to the viewport instead.
 *
 * Phone landscape height is ~393–430px; iPad mini portrait is 1024px,
 * desktop is taller. So 500px cleanly separates "phone rotated" from
 * everything else.
 */
const SHORT_HEIGHT_MEDIA_QUERY = "(max-height: 500px)";

export function useIsShortHeight(): boolean {
  const [isShort, setIsShort] = useState<boolean>(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia(SHORT_HEIGHT_MEDIA_QUERY).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(SHORT_HEIGHT_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsShort(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isShort;
}
